import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Eye, EyeOff, CheckCircle2, XCircle, ArrowLeft, Shield, Cpu, Network, Hexagon, Check, Circle } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { AuthApiService } from '@/features/auth/api/authApi';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  // tRef: o efeito abaixo dispara validateToken por token — `t` não entra nas deps
  // (manual §2.10) senão a troca de idioma refaria a validação do token.
  const tRef = useRef(t);
  tRef.current = t;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(!!token);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // DVD bouncing logo state
  const [dvdPosition, setDvdPosition] = useState({ x: 100, y: 100 });
  const [dvdVelocity, setDvdVelocity] = useState({ dx: 2, dy: 1.5 });
  const [dvdRotation, setDvdRotation] = useState(0);
  const [dvdLogoError, setDvdLogoError] = useState(false);
  const animationFrameRef = useRef<number>();
  const logoSize = 80;

  useEffect(() => {
    if (!token) {
      setError(tRef.current('resetPassword.tokenMissing'));
      return;
    }

    let cancelled = false;
    AuthApiService.validateToken(token, 'PasswordReset')
      .then((isValid) => {
        if (cancelled) return;
        if (!isValid) {
          setError(tRef.current('resetPassword.linkExpiredOrUsed'));
        } else {
          setTokenValid(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setError(tRef.current('resetPassword.linkExpiredOrUsed'));
      })
      .finally(() => {
        if (!cancelled) setIsValidating(false);
      });

    return () => { cancelled = true; };
  }, [token]);

  // DVD bouncing animation
  useEffect(() => {
    const animate = () => {
      setDvdPosition(prev => {
        let newX = prev.x + dvdVelocity.dx;
        let newY = prev.y + dvdVelocity.dy;
        let newDx = dvdVelocity.dx;
        let newDy = dvdVelocity.dy;

        if (newX <= 0 || newX >= window.innerWidth - logoSize) {
          newDx = -newDx;
          newX = Math.max(0, Math.min(newX, window.innerWidth - logoSize));
        }
        if (newY <= 0 || newY >= window.innerHeight - logoSize) {
          newDy = -newDy;
          newY = Math.max(0, Math.min(newY, window.innerHeight - logoSize));
        }

        if (newDx !== dvdVelocity.dx || newDy !== dvdVelocity.dy) {
          setDvdVelocity({ dx: newDx, dy: newDy });
        }

        return { x: newX, y: newY };
      });

      setDvdRotation(prev => (prev + 1) % 360);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [dvdVelocity]);

  const passwordRules = [
    { label: t('shared.passwordRules.min8'), test: (v: string) => v.length >= 8 },
    { label: t('shared.passwordRules.uppercase'), test: (v: string) => /[A-Z]/.test(v) },
    { label: t('shared.passwordRules.number'), test: (v: string) => /[0-9]/.test(v) },
    { label: t('shared.passwordRules.special'), test: (v: string) => /[@$!%*?&#]/.test(v) },
  ];

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return t('shared.passwordRules.min8Error');
    }
    if (!/[A-Z]/.test(pwd)) {
      return t('shared.passwordRules.uppercaseError');
    }
    if (!/[0-9]/.test(pwd)) {
      return t('shared.passwordRules.numberError');
    }
    if (!/[@$!%*?&#]/.test(pwd)) {
      return t('shared.passwordRules.specialError');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setError(t('resetPassword.tokenInvalid'));
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('shared.passwordMismatch'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await AuthApiService.resetPassword({
        token,
        newPassword: password,
        confirmPassword: confirmPassword,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      if (err?.errors) {
        const messages = Object.values(err.errors).flat();
        setError(messages.join('. ') || t('resetPassword.validationErrorFallback'));
      } else {
        setError(err?.detail || err?.message || t('resetPassword.submitErrorFallback'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[@$!%*?&#]/.test(password) && password === confirmPassword;

  const renderCardContent = () => {
    if (isValidating) {
      return (
        <div className="flex flex-col items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mb-4"></div>
          <p className="text-gray-400 text-sm">{t('shared.validating')}</p>
        </div>
      );
    }

    if (!token || (error && !tokenValid)) {
      return (
        <>
          <div className="flex flex-col items-center mb-6">
            <XCircle className="h-16 w-16 text-red-400 mb-4 drop-shadow-[0_0_10px_rgba(248,113,113,0.4)]" />
            <h2 className="text-2xl font-bold text-white">{t('shared.linkExpiredTitle')}</h2>
            <p className="mt-2 text-sm text-gray-400 text-center">
              {error || t('resetPassword.linkInvalidFallback')}
            </p>
          </div>

          <Link to="/forgot-password" className="block">
            <Button className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-primary hover:from-cyan-400 hover:to-primary/90 text-background shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40">
              {t('resetPassword.requestNewLinkButton')}
            </Button>
          </Link>
        </>
      );
    }

    if (success) {
      return (
        <>
          <div className="flex flex-col items-center mb-6">
            <CheckCircle2 className="h-16 w-16 text-emerald-400 mb-4 drop-shadow-[0_0_10px_rgba(52,211,153,0.4)]" />
            <h2 className="text-2xl font-bold text-white">{t('resetPassword.passwordResetTitle')}</h2>
            <p className="mt-2 text-sm text-gray-400 text-center">
              {t('resetPassword.passwordResetDescription')}
            </p>
          </div>

          <Link to="/login" className="block">
            <Button className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-primary hover:from-cyan-400 hover:to-primary/90 text-background shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40">
              {t('shared.goToLoginButton')}
            </Button>
          </Link>
        </>
      );
    }

    return (
      <>
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">{t('resetPassword.title')}</h2>
          <p className="text-gray-400 text-sm">
            {t('resetPassword.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-400">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-gray-300">
              {t('resetPassword.newPasswordLabel')}
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-cyan-400/20"
                required
                autoComplete="new-password"
                disabled={isSubmitting}
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                disabled={isSubmitting}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <div className="text-xs space-y-0.5">
              <p className="text-gray-500">{t('shared.passwordSectionTitle')}</p>
              <ul className="space-y-0.5 ml-1">
                {passwordRules.map((rule) => {
                  const passed = password ? rule.test(password) : false;
                  return (
                    <li key={rule.label} className="flex items-center gap-1.5">
                      {passed ? (
                        <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                      ) : (
                        <Circle className="h-3 w-3 text-gray-600 shrink-0" />
                      )}
                      <span className={passed ? "text-emerald-400" : "text-gray-500"}>
                        {rule.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-300">
              {t('resetPassword.confirmNewPasswordLabel')}
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-cyan-400/20"
                required
                autoComplete="new-password"
                disabled={isSubmitting}
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                disabled={isSubmitting}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-primary hover:from-cyan-400 hover:to-primary/90 text-background shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40"
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-background"></div>
                <span>{t('resetPassword.submitting')}</span>
              </div>
            ) : (
              t('resetPassword.submitButton')
            )}
          </Button>

          <div className="text-center">
            <Link to="/login" className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
              <ArrowLeft className="inline-block mr-1 h-3 w-3" />
              {t('shared.backToLoginLink')}
            </Link>
          </div>
        </form>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-[#1a1f2e] relative overflow-hidden flex items-center justify-center dark">
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

      {/* DVD-style bouncing logo */}
      <div
        className="pointer-events-none fixed z-10"
        style={{
          left: dvdPosition.x,
          top: dvdPosition.y,
          width: logoSize,
          height: logoSize,
          transform: `rotate(${dvdRotation}deg)`,
        }}
      >
        {dvdLogoError ? (
          <Hexagon className="w-full h-full text-cyan-400/20 drop-shadow-[0_0_10px_rgba(0,255,255,0.3)]" />
        ) : (
          <img
            src="/logo-invexis-neon-short.png"
            alt=""
            className="w-full h-full object-contain opacity-20 drop-shadow-[0_0_10px_rgba(0,255,255,0.3)]"
            onError={() => setDvdLogoError(true)}
          />
        )}
      </div>

      {/* Floating icons */}
      <div className="absolute top-20 left-20 text-cyan-400/20 animate-[float_6s_ease-in-out_infinite]">
        <Hexagon className="w-16 h-16" />
      </div>
      <div className="absolute top-40 right-32 text-primary/20 animate-[float_8s_ease-in-out_infinite_1s]">
        <Shield className="w-12 h-12" />
      </div>
      <div className="absolute bottom-32 left-40 text-cyan-400/15 animate-[float_7s_ease-in-out_infinite_0.5s]">
        <Cpu className="w-14 h-14" />
      </div>
      <div className="absolute bottom-20 right-20 text-primary/15 animate-[float_9s_ease-in-out_infinite_1.5s]">
        <Network className="w-20 h-20" />
      </div>

      {/* Blurred background circles */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-400/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/5 rounded-full blur-[100px]" />

      {/* Card */}
      <div className="relative z-20 rounded-2xl p-8 md:p-12 max-w-md w-full mx-4 border border-white/10 bg-[#20263a]/95 animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/logo-invexis-neon.png"
            alt="Invexis Logo"
            className="h-16 md:h-20 drop-shadow-[0_0_15px_rgba(0,255,255,0.5)]"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/logo-invexis.png';
              target.className = 'h-16 md:h-20';
            }}
          />
        </div>

        {renderCardContent()}
      </div>

      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-cyan-400/20 rounded-tl-3xl" />
      <div className="absolute top-0 right-0 w-32 h-32 border-r-2 border-t-2 border-primary/20 rounded-tr-3xl" />
      <div className="absolute bottom-0 left-0 w-32 h-32 border-l-2 border-b-2 border-primary/20 rounded-bl-3xl" />
      <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-cyan-400/20 rounded-br-3xl" />
    </div>
  );
};

export default ResetPassword;
